import { Component, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { BillingFormComponent } from './billing-form.component';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BillableItemType } from '@ee/common/enums';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BillableItemCategory, Court, GenerateBillableItemForm, TeamMember, CheckPayee } from '@ee/common/models';
import { BillableItemCategoryService, UserService } from '@ee/common/services';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ee-billable-item-editor-dialog',
  template: `
    <h2 mat-dialog-title>{{ title }}</h2>

    <form mat-dialog-content class="flex flex-col" [formGroup]="form" (ngSubmit)="save()">
      <ee-billing-form
        class="flex flex-col"
        [billingForm]="form"
        [attorneyFee]="attorneyFee"
        [court]="court"
        [billableItemData]="$safeNavigationMigration(data?.billableItem)"
        [categories]="categories"
        [teamMembers]="teamMembers"
        [showPerformedBy]="showPerformedBy"
        [allowCategoryCostTypeOverride]="allowCategoryCostTypeOverride"
        [legacyBillingAllowed]="legacyBillingAllowed"
        [payees]="payees"></ee-billing-form>
      @if (showPerformedBy && storedPerformedByName && !storedUserInTeam) {
        <div class="performed-by-readonly">Originally performed by: {{ storedPerformedByName }}</div>
      }
    </form>

    <div mat-dialog-actions class="ee-modal-actions">
      <button mat-button type="button" [mat-dialog-close]="null" data-tracking-event="billable-item-editor-cancel">
        CANCEL
      </button>
      <button
        mat-flat-button
        type="submit"
        (click)="save()"
        cdkFocusInitial
        color="accent"
        [disabled]="form.invalid"
        data-tracking-event="billable-item-editor-save">
        SAVE
      </button>
    </div>
  `,
  styles: [
    `
      .performed-by-readonly {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.54);
        margin-bottom: 8px;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BillableItemEditorDialogComponent implements OnInit, OnDestroy {
  @ViewChild(BillingFormComponent) billingFormComponent: BillingFormComponent;

  form: UntypedFormGroup;
  billableItemType = BillableItemType;
  title = 'Edit Billable Item';
  attorneyFee: number | undefined;
  court: Court | undefined;
  teamMembers: TeamMember[] = [];
  showPerformedBy = false;
  allowCategoryCostTypeOverride = false;
  legacyBillingAllowed = true;
  storedPerformedByName: string | null = null;
  storedUserInTeam = true;
  categories: BillableItemCategory[] = [];
  payees: CheckPayee[] = [];

  private subs: Subscription[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<BillableItemEditorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userService: UserService,
    private categoryService: BillableItemCategoryService
  ) {
    this.form = GenerateBillableItemForm(formBuilder, data.billableItem);
    this.attorneyFee = data.attorneyFee;
    this.court = data.court;
    this.showPerformedBy = data.showPerformedBy ?? false;
    this.allowCategoryCostTypeOverride = data.allowCategoryCostTypeOverride ?? false;
    // Defaults to allowed; an existing Legacy item still shows correctly via effectiveLegacyAllowed.
    this.legacyBillingAllowed = data.legacyBillingAllowed ?? true;
    this.payees = data.payees ?? [];
    // override title only if one is passed in
    if (data.title) {
      this.title = data.title;
    }

    // Capture stored performed_by for read-only fallback
    if (data.billableItem?.performed_by) {
      this.storedPerformedByName = data.billableItem.performed_by;
    }
  }

  ngOnInit() {
    this.subs.push(
      this.userService.getTeamMembers().subscribe((members) => {
        this.teamMembers = members;
        // Check if stored performed_by_user_id matches any active team member
        const storedUserId = this.data.billableItem?.performed_by_user_id;
        if (storedUserId) {
          this.storedUserInTeam = members.some((m) => m.id === storedUserId);
        }
      })
    );

    this.subs.push(
      this.categoryService.getCategories().subscribe((categories) => {
        this.categories = categories;
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  save() {
    if (this.form.get('type').value === this.billableItemType.TIME_ENTRY) {
      this.form.patchValue({ amount: this.form.get('qty').value * this.form.get('rate').value });
    } else {
      this.form.patchValue({ qty: 1, rate: this.form.get('amount').value });
    }
    this.dialogRef.close(this.form.value);
  }
}
